import { Maximize2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useContentAreaSize } from "../../../../../size-context";
import { NestedSnippetProvider } from "../../../nested-snippet-context";
import { getSnippetSizeClasses } from "../../../utils";

import { SnippetFullscreenDialog } from "./SnippetFullscreenDialog";

import ContentArea from "@/components/ContentArea";
import { TemplateFormat } from "@/components/Playground/types";
import { Button } from "@/components/ui/button";

interface SnippetContentProps {
  prompt: string;
  onPromptChange: (value: string) => void;
  hasChanged: boolean;
  isReadOnly?: boolean;
  promptName: string;
  version?: number;
  label?: string;
  format?: TemplateFormat;
}

const SnippetContent = ({
  prompt,
  onPromptChange,
  hasChanged,
  isReadOnly = false,
  promptName,
  version,
  label,
  format,
}: SnippetContentProps) => {
  const size = useContentAreaSize();

  const isChangeOrigin = useRef(false);

  const [key, setKey] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!isChangeOrigin.current) {
      setKey(key + 1);
      isChangeOrigin.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt]);

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    isChangeOrigin.current = true;
    onPromptChange?.(e.target.value);
    setTimeout(() => {
      isChangeOrigin.current = false;
    }, 0);
  };

  return (
    <div className="group/SnippetContent relative">
      <Button
        variant="ghost"
        size="tinyIcon"
        className="absolute right-2 top-2 z-50 opacity-0 transition-opacity group-hover/SnippetContent:opacity-100"
        onClick={e => {
          e.stopPropagation();
          setIsFullscreen(true);
        }}
      >
        <Maximize2 className="h-4 w-4 text-gray-600 hover:text-gray-900" />
      </Button>
      <div
        className={`${getSnippetSizeClasses(
          size,
          "container",
        )} min-w-0 max-w-full cursor-text overflow-y-auto rounded-b-md border border-t-0 border-dashed text-left ${
          hasChanged ? "border-amber-400" : "border-blue-400"
        }`}
      >
        <NestedSnippetProvider snippetName={promptName}>
          <ContentArea
            key={key}
            value={prompt}
            config={{
              format,
            }}
            onChange={onChange}
            className="w-full min-w-0 !whitespace-pre border-0 bg-transparent p-0 shadow-none"
            containerClassName="min-w-0 max-h-none"
            size="xs"
            readOnly={isReadOnly}
          />
        </NestedSnippetProvider>
      </div>
      <SnippetFullscreenDialog
        isOpen={isFullscreen}
        onOpenChange={setIsFullscreen}
        prompt={prompt}
        onPromptChange={onPromptChange}
        hasChanged={hasChanged}
        isReadOnly={isReadOnly}
        promptName={promptName}
        version={version}
        label={label}
        format={format}
      />
    </div>
  );
};

export default SnippetContent;
